import { useGetCompany, getGetCompanyQueryKey, useUpdateCompany, useListFounders, useListDocuments, useListMedia, useListUpdates, useListKpis, getListKpisQueryKey, useListAuditLog, useListInvestorAccess } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Globe, MapPin, Calendar, Percent, Users, FileText, Video, BarChart3, Activity, Shield, Sparkles, Wand2, Loader2 } from "lucide-react";
import InfoTip from "@/components/info-tip";
import { useState } from "react";

const stageBadgeStyles: Record<string, string> = {
  pipeline: "bg-blue-100 text-blue-800",
  onboarding: "bg-amber-100 text-amber-800",
  active: "bg-emerald-100 text-emerald-800",
  exited: "bg-gray-100 text-gray-800",
};

const updateStatusStyles: Record<string, string> = {
  draft: "bg-gray-100 text-gray-800",
  pending_review: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  published: "bg-blue-100 text-blue-800",
};

export default function CompanyDetail({ id, cohortSlug }: { id: number; cohortSlug?: string }) {
  const { data: company, isLoading } = useGetCompany(id, { query: { enabled: !!id, queryKey: getGetCompanyQueryKey(id) } });
  const { data: founders } = useListFounders({ companyId: id });
  const { data: documents } = useListDocuments({ companyId: id });
  const { data: media } = useListMedia({ companyId: id });
  const { data: updates } = useListUpdates({ companyId: id });
  const { data: kpis } = useListKpis({ companyId: id }, { query: { enabled: !!id, queryKey: getListKpisQueryKey({ companyId: id }) } });
  const { data: auditLog } = useListAuditLog({ companyId: id, limit: 20 });
  const { data: investorAccess } = useListInvestorAccess({ companyId: id });
  const [parsingThesis, setParsingThesis] = useState(false);
  const [parseStatus, setParseStatus] = useState<{ kind: "success" | "error"; msg: string } | null>(null);

  const handleParseThesis = async () => {
    setParsingThesis(true);
    setParseStatus(null);
    try {
      const adminToken = localStorage.getItem("adminToken") || "";
      const res = await fetch(`/api/companies/${id}/parse-thesis`, {
        method: "POST",
        headers: { "x-admin-token": adminToken },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setParseStatus({ kind: "error", msg: body?.error || `Parse failed (HTTP ${res.status})` });
      } else {
        setParseStatus({ kind: "success", msg: `Thesis structured (${body?.structured?.modelUsed || "Favcy Navigator"}). Investor portal will render bullets.` });
      }
    } catch (err: any) {
      setParseStatus({ kind: "error", msg: err?.message || "Network error" });
    } finally {
      setParsingThesis(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40" />
        <Skeleton className="h-60" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Company not found</p>
        <Link href={cohortSlug ? `/c/${cohortSlug}/companies` : "/companies"}><span className="text-primary hover:underline text-sm mt-2 inline-block cursor-pointer">Back to companies</span></Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="page-company-detail">
      <div className="flex items-center gap-3">
        <Link href={cohortSlug ? `/c/${cohortSlug}/companies` : "/companies"}>
          <button className="p-1.5 rounded-md hover:bg-muted transition-colors" data-testid="button-back-companies">
            <ArrowLeft className="w-4 h-4" />
          </button>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-serif font-bold tracking-tight">{company.name}</h1>
            <InfoTip content="This is the complete company profile — everything the ops team knows about this company. Tabs let you explore weekly updates, KPIs, team members, documents, media, investor access controls, and the full audit trail. Changes here are tracked and audited." />
            <Badge className={`text-xs ${stageBadgeStyles[company.stage] || ""}`}>{company.stage}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{company.sector}</p>
        </div>
        <Link href={cohortSlug ? `/c/${cohortSlug}/companies/${company.id}/showcase` : "#"}>
          <Button variant="outline" size="sm" className="gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> View Showcase
          </Button>
        </Link>
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {company.website && (
              <div className="flex items-center gap-2 text-sm">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate" data-testid="link-company-website">{company.website}</a>
              </div>
            )}
            {company.location && (
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <span>{company.location}</span>
              </div>
            )}
            {company.foundedYear && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span>Founded {company.foundedYear}</span>
              </div>
            )}
            {company.equityPercent != null && (
              <div className="flex items-center gap-2 text-sm">
                <Percent className="w-4 h-4 text-muted-foreground" />
                <span>{company.equityPercent}% equity</span>
              </div>
            )}
          </div>
          {company.thesis && (
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center justify-between mb-2 gap-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Investment Thesis</p>
                <Button size="sm" variant="outline" onClick={handleParseThesis} disabled={parsingThesis}
                  data-testid="button-parse-thesis"
                  className="h-7 text-xs">
                  {parsingThesis ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1.5" />}
                  {parsingThesis ? "Structuring…" : "Re-structure thesis"}
                </Button>
              </div>
              <p className="text-sm leading-relaxed">{company.thesis}</p>
              {parseStatus && (
                <p className={`mt-2 text-xs ${parseStatus.kind === "error" ? "text-destructive" : "text-emerald-700"}`}>
                  {parseStatus.msg}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="updates" className="w-full">
        <TabsList className="w-full justify-start" data-testid="tabs-company-detail">
          <TabsTrigger value="updates">Updates ({updates?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="kpis">KPIs ({kpis?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="founders">Team ({founders?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="documents">Documents ({documents?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="media">Media ({media?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="access">Access ({investorAccess?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="audit">Audit</TabsTrigger>
        </TabsList>

        <TabsContent value="updates" className="mt-4">
          {updates && updates.length > 0 ? (
            <div className="space-y-4">
              {updates.map((update) => (
                <Card key={update.id} data-testid={`card-update-${update.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Week of {new Date(update.weekOf).toLocaleDateString()}</span>
                        <Badge className={`text-xs ${updateStatusStyles[update.status] || ""}`}>{update.status.replace("_", " ")}</Badge>
                      </div>
                      {update.approvedBy && (
                        <span className="text-xs text-muted-foreground">Approved by {update.approvedBy}</span>
                      )}
                    </div>
                    {update.highlights && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Highlights</p>
                        <p className="text-sm">{update.highlights}</p>
                      </div>
                    )}
                    {update.challenges && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Challenges</p>
                        <p className="text-sm">{update.challenges}</p>
                      </div>
                    )}
                    {update.asks && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Asks</p>
                        <p className="text-sm">{update.asks}</p>
                      </div>
                    )}
                    {update.metrics && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Metrics</p>
                        <p className="text-sm font-mono">{update.metrics}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No updates yet</CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="kpis" className="mt-4">
          {kpis && kpis.length > 0 ? (
            <Card>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-medium text-muted-foreground text-xs uppercase">Metric</th>
                      <th className="text-right p-3 font-medium text-muted-foreground text-xs uppercase">Value</th>
                      <th className="text-left p-3 font-medium text-muted-foreground text-xs uppercase">Period</th>
                      <th className="text-left p-3 font-medium text-muted-foreground text-xs uppercase">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kpis.map((kpi) => (
                      <tr key={kpi.id} className="border-b last:border-0" data-testid={`row-kpi-${kpi.id}`}>
                        <td className="p-3 font-medium">{kpi.metric}</td>
                        <td className="p-3 text-right font-mono">{kpi.value.toLocaleString()}{kpi.unit ? ` ${kpi.unit}` : ""}</td>
                        <td className="p-3 text-muted-foreground">{kpi.period}</td>
                        <td className="p-3"><Badge variant="outline" className="text-xs">{kpi.source ?? "manual"}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ) : (
            <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No KPIs recorded</CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="founders" className="mt-4">
          {founders && founders.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {founders.map((founder) => (
                <Card key={founder.id} data-testid={`card-founder-${founder.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium">{founder.name}</h4>
                        <p className="text-xs text-muted-foreground">{founder.role}</p>
                      </div>
                    </div>
                    {founder.bio && <p className="text-sm text-muted-foreground mt-3">{founder.bio}</p>}
                    <p className="text-xs text-muted-foreground mt-2">{founder.email}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No founders added</CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          {documents && documents.length > 0 ? (
            <div className="space-y-2">
              {documents.map((doc) => (
                <Card key={doc.id} data-testid={`card-doc-${doc.id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">{doc.title}</p>
                        <p className="text-xs text-muted-foreground">{doc.category.replace("_", " ")} {doc.uploadedBy ? `| Uploaded by ${doc.uploadedBy}` : ""}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {doc.visibleToInvestors && <Badge variant="outline" className="text-xs"><Shield className="w-3 h-3 mr-1" />Investor visible</Badge>}
                      <span className="text-xs text-muted-foreground">{new Date(doc.createdAt).toLocaleDateString()}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No documents uploaded</CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="media" className="mt-4">
          {media && media.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {media.map((asset) => (
                <Card key={asset.id} data-testid={`card-media-${asset.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <Video className="w-4 h-4 text-muted-foreground" />
                      <Badge variant="outline" className="text-xs capitalize">{asset.type.replace("_", " ")}</Badge>
                    </div>
                    <h4 className="font-medium text-sm">{asset.title}</h4>
                    {asset.description && <p className="text-xs text-muted-foreground mt-1">{asset.description}</p>}
                    <a href={asset.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline mt-2 inline-block">View asset</a>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No media assets linked</CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="access" className="mt-4">
          {investorAccess && investorAccess.length > 0 ? (
            <div className="space-y-2">
              {investorAccess.map((access) => (
                <Card key={access.id} data-testid={`card-access-${access.id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Investor #{access.investorId}</p>
                      <p className="text-xs text-muted-foreground">Granted {new Date(access.grantedAt).toLocaleDateString()}</p>
                    </div>
                    <Badge variant="outline" className="text-xs capitalize">{access.accessLevel} access</Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No investors have access</CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          {auditLog && auditLog.length > 0 ? (
            <div className="space-y-1">
              {auditLog.map((entry) => (
                <div key={entry.id} className="flex items-start gap-3 py-2 border-b border-border last:border-0" data-testid={`audit-entry-${entry.id}`}>
                  <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <div>
                    <p className="text-sm">{entry.details || `${entry.entity} ${entry.action}`}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {entry.performedBy && <span className="text-xs text-muted-foreground">by {entry.performedBy}</span>}
                      {entry.source && <Badge variant="outline" className="text-xs">{entry.source}</Badge>}
                      <span className="text-xs text-muted-foreground">{new Date(entry.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No audit entries</CardContent></Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
